<template>
  <div class="print-page">
    <div class="print-toolbar no-print">
      <button @click="window.print()" class="btn btn-primary"><i class="fas fa-print"></i> 打印</button>
      <button @click="window.close()" class="btn btn-default"><i class="fas fa-times"></i> 关闭</button>
    </div>

    <div v-if="loading" class="loading-state"><i class="fas fa-spinner fa-spin"></i> 加载打印数据中...</div>
    <div v-else-if="error" class="error-state">{{ error }}</div>

    <div v-else class="print-content">
      <!-- 任务信息头 -->
      <div class="print-header">
        <h2>{{ printData.processName || '任务打印' }}</h2>
        <div class="print-meta">
          <span>流水号: {{ printData.serialNum || '-' }}</span>
          <span>发起时间: {{ fmtDate(printData.createAt) }}</span>
          <span>描述: {{ printData.description || '-' }}</span>
        </div>
      </div>

      <!-- 打印模版（表单渲染） -->
      <div class="print-template-section" v-if="templateFields.length > 0">
        <h3 class="section-title">表单数据</h3>
        <table class="template-table">
          <tbody>
            <tr v-for="(field, idx) in templateFields" :key="idx">
              <td class="field-label">{{ field.label }}</td>
              <td class="field-value">{{ field.value }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 业务数据表格 -->
      <div class="print-data-section" v-if="mainTables.length > 0">
        <h3 class="section-title">业务数据</h3>
        <template v-for="entry in mainTables" :key="entry.tableName">
          <h4 class="table-name">{{ entry.tableName }}</h4>
          <table class="data-table" v-if="entry.rows.length > 0">
            <thead>
              <tr><th v-for="key in entry.columns" :key="key">{{ key }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in entry.rows" :key="ri">
                <td v-for="key in entry.columns" :key="key">{{ formatVal(row[key]) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">暂无数据</p>
        </template>
      </div>

      <!-- 明细数据 -->
      <div class="print-data-section" v-if="detailTables.length > 0">
        <h3 class="section-title">明细数据</h3>
        <template v-for="entry in detailTables" :key="entry.tableName">
          <h4 class="table-name">{{ entry.tableName }}</h4>
          <table class="data-table" v-if="entry.rows.length > 0">
            <thead>
              <tr><th v-for="key in entry.columns" :key="key">{{ key }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in entry.rows" :key="ri">
                <td v-for="key in entry.columns" :key="key">{{ formatVal(row[key]) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">暂无数据</p>
        </template>
      </div>

      <!-- 审批历史 -->
      <div class="print-data-section" v-if="printData.history && printData.history.length > 0">
        <h3 class="section-title">审批历史</h3>
        <table class="data-table">
          <thead>
            <tr><th>步骤</th><th>处理人</th><th>动作</th><th>处理时间</th><th>意见</th></tr>
          </thead>
          <tbody>
            <tr v-for="h in printData.history" :key="h.StepId || h.stepId">
              <td>{{ h.NodeName || h.nodeName || '-' }}</td>
              <td>{{ h.HandlerName || h.handlerName || '-' }}</td>
              <td>{{ h.ActionLinkName || h.actionLinkName || '-' }}</td>
              <td>{{ fmtDate(h.FinishOn || h.finishOn) }}</td>
              <td>{{ h.Memo || h.memo || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getPrintData } from '../api/task'

const loading = ref(true)
const error = ref('')
const printData = ref({})

const fmtDate = (val) => {
  if (!val) return '-'
  return String(val).replace('T', ' ').substring(0, 19)
}

const formatVal = (v) => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v ? '是' : '否'
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.replace('T', ' ').substring(0, 19)
  return s
}

// 解析打印模版表单字段
const templateFields = computed(() => {
  const content = printData.value?.printFormContent
  if (!content) return []
  try {
    const controls = typeof content === 'string' ? JSON.parse(content) : content
    if (!Array.isArray(controls)) return []
    
    // 获取业务数据中第一行的值，用于填充模版字段
    const allMainValues = {}
    const main = printData.value?.tableData?.main
    if (main) {
      Object.values(main).forEach(rowData => {
        if (rowData && typeof rowData === 'object') {
          Object.entries(rowData).forEach(([k, v]) => { allMainValues[k] = v })
        }
      })
    }

    return controls.map(ctrl => {
      const label = ctrl.label || ctrl.title || ctrl.name || ctrl.id || ''
      const fieldName = ctrl.fieldName || ctrl.bindField || ctrl.id || ''
      // 尝试从业务数据中查找值
      let value = allMainValues[fieldName] ?? ''
      if (!value && ctrl.id) {
        // 尝试各种 key 格式
        value = allMainValues[ctrl.id] ?? allMainValues[ctrl.id.toLowerCase()] ?? ''
      }
      return { label, value: formatVal(value) }
    }).filter(f => f.label)
  } catch (e) {
    console.warn('Failed to parse print template:', e)
    return []
  }
})
const mainTables = computed(() => {
  const main = printData.value?.tableData?.main
  if (!main) return []
  return Object.entries(main).map(([tableName, rowData]) => {
    const cols = Object.keys(rowData || {})
    return { tableName, columns: cols, rows: [rowData] }
  })
})

// 解析明细表数据
const detailTables = computed(() => {
  const detail = printData.value?.tableData?.detail
  if (!detail) return []
  return Object.entries(detail).map(([tableName, rows]) => {
    const allCols = new Set()
    ;(rows || []).forEach(row => Object.keys(row || {}).forEach(k => allCols.add(k)))
    return { tableName, columns: [...allCols], rows: rows || [] }
  })
})

onMounted(async () => {
  const pathParts = window.location.pathname.split('/')
  const taskId = pathParts[pathParts.length - 1]
  if (!taskId) { error.value = '任务ID不存在'; loading.value = false; return }

  try {
    const res = await getPrintData(taskId)
    const data = res?.data ?? res?.Data ?? res
    if (data && Object.keys(data).length > 0) {
      printData.value = data
    } else {
      error.value = '无法获取打印数据'
    }
  } catch (e) {
    error.value = '获取打印数据失败: ' + (e.message || '未知错误')
  } finally {
    loading.value = false
  }

  // 自动触发打印（可选）
  // setTimeout(() => window.print(), 500)
})
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif; font-size: 13px; color: #333; }

.print-page { padding: 10px; max-width: 1000px; margin: 0 auto; }

.print-toolbar { display: flex; gap: 8px; margin-bottom: 20px; padding: 10px; background: #f9fafb; border-radius: 6px; }
.btn { padding: 6px 16px; border-radius: 4px; border: none; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; gap: 5px; }
.btn-primary { background: #1ab394; color: #fff; }
.btn-default { background: #e5e7eb; color: #374151; }

.print-header { margin-bottom: 20px; padding-bottom: 12px; border-bottom: 2px solid #1ab394; }
.print-header h2 { font-size: 18px; margin-bottom: 8px; color: #1a1a1a; }
.print-meta { display: flex; gap: 20px; color: #6b7280; font-size: 13px; }

.print-template-section { margin-bottom: 24px; }
.template-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
.template-table td { border: 1px solid #d1d5db; padding: 6px 10px; font-size: 13px; }
.field-label { background: #f3f4f6; font-weight: 600; width: 140px; color: #4b5563; }
.field-value { color: #1a1a1a; }

.print-data-section { margin-bottom: 24px; }
.section-title { font-size: 15px; color: #1a1a1a; margin-bottom: 10px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
.table-name { font-size: 13px; color: #4b5563; margin: 8px 0 4px; }

.data-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
.data-table th, .data-table td { border: 1px solid #d1d5db; padding: 6px 10px; text-align: left; font-size: 12px; }
.data-table th { background: #f3f4f6; font-weight: 600; }
.data-table tbody tr:nth-child(even) { background: #f9fafb; }
.no-data { color: #9ca3af; font-size: 12px; padding: 8px 0; }

.loading-state, .error-state { text-align: center; padding: 60px 0; color: #6b7280; font-size: 14px; }

@media print {
  .no-print { display: none !important; }
  .print-page { padding: 0; }
}
</style>
